import {
  ProfessionalApplicationStatusEnum,
  ReviewProData,
  ReviewProDataApplicationStatus,
  ReviewProCohortHistory,
} from '@platform/client/schemas';
import {
  ProfileItem,
  ProfileItemContainer,
  ProfileItemLong,
  ProfileLabel,
  ProfileLinkExternal,
  ProfileSection,
  ProfileTabContainer,
  ProfileValueColors,
  TeamProfileLink,
} from '../../components/ProfileComponents';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { useState } from 'react';

export const ProfileTabContent = ({ proData }: { proData?: ReviewProData }) => {
  if (!proData) {
    return <div>No professional data available.</div>;
  }
  return (
    <ProfileTabContainer>
      <ProfileSection title='Professional Details'>
        <ProfileItem label='City' value={proData.city} />
        <ProfileLinkExternal label='LinkedIn' href={proData.linkedin} />
        <ProfileItem label='Country' value={proData.country} />
        <ProfileLinkExternal
          label='Email'
          href={`mailto:${proData.email}`}
          displayName={proData.email}
        />
        <ProfileItem label='Employer' value={proData.employer} />
        <ProfileLinkExternal label='Resume' href={proData.resume} />
        <ProfileItem label='Job Title' value={proData.jobTitle} />
        <ProfileItem label='Corporate Client' value={proData.corporateClient} />
        <ProfileItem
          label='Experience Years'
          value={proData.experienceYears ? proData.experienceYears.toString() : ''}
        />
        <CohortValueItem currentCohort={proData.cohortName} cohortHistory={proData.cohortHistory} />
        <ProfileItem label='Work Type' value={proData.workType} />
        <ProfileItem label='Team Opt In' value={proData.teamOptIn ? 'Yes' : 'No'} />
        <ProfileItem label='Board Area' value={proData.boardArea} />
        <TeamProfileLink cohortId={proData.cohortId} teamId={proData.teamId} />
        <ApplicationStatusItem
          applicationStatus={proData.applicationStatus}
          isDisabled={proData.disabledDate !== null}
        />
        <ProfileItem
          label='Application Date'
          value={proData.applicationDate ? formatDateStr(proData.applicationDate) : 'N/A'}
        />
      </ProfileSection>
      <ProfileSection title='Professional Skills' columns={1}>
        <ProfileItem label='Languages' value={proData.languages?.join(', ')} />
        <ProfileItem label='Skills' value={proData.skills?.join(', ')} />
        <ProfileItem label='SDGs' value={proData.causes?.join(', ')} />
        <ProfileItem label='Industries' value={proData.industries?.join(', ')} />
        <ProfileItemLong label='Skills Explanation' value={proData.skillsExplanation} />
      </ProfileSection>
    </ProfileTabContainer>
  );
};

const ApplicationStatusItem = ({
  applicationStatus,
  isDisabled = false,
}: {
  applicationStatus?: ReviewProDataApplicationStatus;
  isDisabled?: boolean;
}) => {
  let color: ProfileValueColors = 'default';
  if (isDisabled || applicationStatus === ProfessionalApplicationStatusEnum.rejected) {
    color = 'error';
  } else if (applicationStatus === ProfessionalApplicationStatusEnum.approved) {
    color = 'success';
  } else if (applicationStatus === ProfessionalApplicationStatusEnum.waitlisted) {
    color = 'warning';
  }
  return (
    <ProfileItem
      label='Application Status'
      value={isDisabled ? 'Disabled' : applicationStatus}
      color={color}
    />
  );
};

const CohortValueItem = ({
  currentCohort,
  cohortHistory,
}: {
  currentCohort?: string | null;
  cohortHistory?: ReviewProCohortHistory[] | null;
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };
  const showToggle = (cohortHistory?.length ?? 0) > 0;
  return (
    <ProfileItemContainer>
      <ProfileLabel label='Cohort' />
      <div className='text-sm text-gray-700 font-medium'>
        <p>
          {currentCohort ? `${currentCohort} - Active` : 'None'}
          {showToggle && (
            <button
              aria-label='toggle-cohort-history'
              onClick={toggleHistory}
              className='text-blue-500 hover:underline ml-2 text-xs'
            >
              ({showHistory ? 'Hide History' : 'Show History'})
            </button>
          )}
        </p>
        {showHistory &&
          cohortHistory?.map((ch) => (
            <p
              key={ch.name}
            >{`${ch.name} - ${ch.status.charAt(0).toUpperCase() + ch.status.slice(1)}`}</p>
          ))}
      </div>
    </ProfileItemContainer>
  );
};
